import { DealCard } from "~/components/common/DealCard";
import type { DealClientListData } from "~/types/deal";
import { Button } from "~/components/ui/button";
import { ChevronDown } from "lucide-react";

interface DealFeedProps {
  deals: DealClientListData[];
  onDealClick?: (deal: DealClientListData) => void;
  onLoadMore?: () => void;
  getBadgeText?: (deal: DealClientListData, index: number) => string | undefined;
  isLoading: boolean;
  hasMore: boolean;
  showNoMore?: boolean;
  className?: string;
}

export function DealFeed({
  deals,
  onDealClick,
  onLoadMore,
  getBadgeText,
  isLoading = false,
  hasMore = false,
  showNoMore = true,
  className = "",
}: DealFeedProps) {
  const handleDealClick = (deal: DealClientListData) => {
    if (onDealClick) {
      onDealClick(deal);
    } else {
      // open in new tab
      window.open(`/deal/detail?deal_id=${deal._id}`, "_blank");
    }
  };

  // Implement manual load more button
  const handleLoadMore = () => {
    if (onLoadMore) {
      onLoadMore();
    }
  };

  return (
    <div className={className}>
      {/* Deal Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {deals.map((deal, index) => {
          const badgeText = getBadgeText ? getBadgeText(deal, index) : undefined;
          return (
            <DealCard
              key={`${deal._id}-${index}`}
              deal={deal}
              badgeText={badgeText}
              onClick={() => handleDealClick(deal)}
            />
          );
        })}
      </div>

      {/* Load more button */}
      {onLoadMore && hasMore && !isLoading && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={handleLoadMore}>
            View more <ChevronDown className="ml-2" />
          </Button>
        </div>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <div className="mt-6 flex justify-center">
          <div className="border-t-primary h-6 w-6 animate-spin rounded-full border-4 border-gray-200" />
        </div>
      )}

      {/* End of deals indicator */}
      {!hasMore && deals.length > 0 && showNoMore && (
        <div className="mt-6 flex justify-center">
          <div className="text-gray-500">No more deals to load</div>
        </div>
      )}
    </div>
  );
}
